import Vue from 'vue';
import Router from 'vue-router';
import LoginPage from './views/LoginPage';
import HomePage from './views/HomePage';
import AboutPage from './views/AboutPage';
import store from './store';

Vue.use(Router);

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
        {
            path: '/',
            name: 'homePage',
            component: HomePage,
            meta: {
                title: 'Home page',
                requiresAuth: false
            },
            redirect: '/devices'
        },
        {
            path: '/about',
            name: 'aboutPage',
            component: AboutPage,
            meta: {
                title: 'About page',
                requiresAuth: true,
                ignorePermissions: true
            }
        },
        {
            path: '/login',
            name: 'loginPage',
            component: LoginPage,
            meta: {
                title: 'Login page',
                requiresAuth: false
            }
        },
        {
            path: '/spaces',
            name: 'spacesPage',
            component: () => {
                return import(
                    /* webpackChunkName: "spaces" */ './views/spaces/SpacesPage.vue'
                );
            },
            meta: {
                title: 'Spaces page',
                requiresAuth: true
            }
        },
        {
            path: '/devices',
            name: 'devicesPage',
            component: () => {
                return import(
                    /* webpackChunkName: "devices" */ './views/devices/DevicesPage.vue'
                );
            },
            meta: {
                title: 'Devices page',
                requiresAuth: true
            }
        },
        {
            path: '/tags',
            name: 'tagsPage',
            component: () => {
                return import(
                    /* webpackChunkName: "tags" */ './views/tags/TagsPage.vue'
                );
            },
            meta: {
                title: 'Tags page',
                requiresAuth: true
            }
        },
        {
            path: '/users',
            name: 'usersPage',
            component: () => {
                return import(
                    /* webpackChunkName: "users" */ './views/users/UsersPage.vue'
                );
            },
            meta: {
                title: 'Users page',
                requiresAuth: true
            }
        },
        {
            path: '/user-profile',
            name: 'userProfilePage',
            component: () => {
                return import(
                    /* webpackChunkName: "profile" */ './views/users/UserProfilePage.vue'
                );
            },
            meta: {
                title: 'User Profile page',
                requiresAuth: true,
                ignorePermissions: true
            }
        },
        {
            path: '/activities',
            name: 'activitiesPage',
            component: () => {
                return import(
                    /* webpackChunkName: "activities" */ './views/activities/ActivitiesPage.vue'
                );
            },
            meta: {
                title: 'Recent Activities page',
                requiresAuth: true
            }
        },
        {
            path: '/support',
            name: 'supportPage',
            component: () => {
                return import(
                    /* webpackChunkName: "support" */ './views/support/SupportPage.vue'
                );
            },
            meta: {
                title: 'Customer Support page',
                requiresAuth: true
            }
        },
        {
            path: '/settings',
            name: 'settingsPage',
            component: () => {
                return import(
                    /* webpackChunkName: "settings" */ './views/settings/SettingsPage.vue'
                );
            },
            meta: {
                title: 'Venue Settings page',
                requiresAuth: true
            }
        },
        {
            path: '/venue-switcher',
            name: 'venueSwitcherPage',
            component: () => {
                return import(
                    /* webpackChunkName: "venues" */ './views/venues/VenueSwitcherPage.vue'
                );
            },
            meta: {
                title: 'Venue Switcher page',
                requiresAuth: true,
                ignorePermissions: true
            }
        },
        {
            path: '/change-password',
            name: 'changePasswordPage',
            component: () => {
                return import(
                    /* webpackChunkName: "login" */ './views/ChangePasswordPage.vue'
                );
            },
            meta: {
                title: 'Change Password page',
                requiresAuth: false
            }
        }
    ]
});

router.beforeEach(async (to, from, next) => {
    console.log('Entering Route: ', to);
    checkUrlParams(to);
    if (to.matched.some(record => record.meta.requiresAuth)) {
        doAuthenticatedRoute(to, from, next);
    } else if (to.matched.length === 0) {
        doUndefinedRoute(to, from, next);
    } else {
        doUnauthenticatedRoute(to, from, next);
    }
});

const doAuthenticatedRoute = async (to, from, next) => {
    // need to check if the userToken is not expired
    // then we need to login again
    const isAuthenticated = store.getters['authUser/isUserAuthenticated'];
    const selectedVenue = store.getters['venue/currentVenue'];
    const selectedVenueInfo = store.state.venue.selectedVenueInfo;
    if (isAuthenticated) {
        if (selectedVenue && router.app.$vuetify.breakpoint.lgAndUp) {
            store.commit('setNavDrawer', true);
        }
        if (to.name !== 'venueSwitcherPage' && !selectedVenue) {
            next({
                name: 'venueSwitcherPage',
                query: { redirect: to.path }
            });
            return;
        }
        // need to get the venueInfo
        if (
            to.name !== 'venueSwitcherPage' &&
            !Object.keys(selectedVenueInfo).length
        ) {
            await store.dispatch('venue/fetchVenueInfo');
        }
        // check if the user has permission for this route
        if (
            !to.matched.some(record => record.meta.ignorePermissions) &&
            !router.app.$can('route', to.path)
        ) {
            next({ name: 'homePage' });
            return;
        }
        next();
        return;
    }
    next({
        name: 'loginPage',
        query: { ...to.query, redirect: to.path }
    });
};

const doUnauthenticatedRoute = async (to, from, next) => {
    // ============ Unauthenticated routes =============
    const isAuthenticated = store.getters['authUser/isUserAuthenticated'];
    store.commit('setNavDrawer', false);
    if (!isAuthenticated || to.name === 'changePasswordPage') {
        const redirect = checkRedirectUrl();
        if (to.query.code) {
            // check if the is xerxes auth_code query param
            await store.dispatch('authUser/convertToken', to.query.code);
            next({ ...to, query: { redirect } });
            return;
        }
        if (to.query.user_token) {
            // check if there is a user_token query param
            await store.dispatch('authUser/saveUserToken', to.query.user_token);
            await store.dispatch('authUser/getUserInfo');
            next({ ...to, query: { redirect } });
            return;
        }
        if (to.name === 'changePasswordPage' && to.query.token) {
            await store.dispatch('authUser/saveAuthToken', to.query.token);
            next({ ...to, query: {} });
            return;
        }
        next();
        return;
    }
    if (to.query.redirect) {
        next(to.query.redirect);
    } else {
        next({ name: 'homePage' });
    }
};

const doUndefinedRoute = (to, from, next) => {
    const externalRoutes = ['/admin', '/user-guide'];
    if (externalRoutes.includes(to.path)) {
        window.location.pathname = to.path;
    } else {
        next({ name: 'loginPage' });
    }
};

const checkUrlParams = to => {
    // Will check for URL query params and save them
    let clearQuery = false;
    const earlyAccess = to.query.earlyAccess;
    if (earlyAccess && ['true', 'false'].includes(earlyAccess.toLowerCase())) {
        store.dispatch('saveEarlyAccess', JSON.parse(earlyAccess));
        clearQuery = true;
    }
    const targetHost = to.query.targetHost;
    if (targetHost) {
        store.dispatch('brand/saveMso', targetHost);
        clearQuery = true;
    }
    if (clearQuery) {
        setTimeout(() => {
            router.replace({ query: null });
        }, 100);
    }
};

const checkRedirectUrl = () => {
    const redirect = localStorage.getItem('redirect');
    if (redirect) {
        localStorage.removeItem('redirect');
    }
    return redirect;
};

router.afterEach(async to => {
    if (to.matched.some(record => record.meta.requiresAuth)) {
        await store.dispatch('notifier/loading', true);
        setTimeout(async () => {
            await store.dispatch('notifier/loading', false);
        }, 1000);
    }
});

export default router;
